import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query';
import { supabase } from '@/integrations/supabase/client';
import { toast } from 'sonner';

export interface LanguagePack {
  id: string;
  tenant_id?: string;
  language_code: string;
  language_name: string;
  status: 'draft' | 'complete' | 'incomplete';
  translations: Record<string, any>;
  completion_percentage: number;
  is_system_default: boolean;
  created_by?: string;
  created_at: string;
  updated_at: string;
}

export interface CreateLanguagePackData {
  language_code: string;
  language_name: string;
  translations?: Record<string, any>;
}

export const useLanguagePacks = () => {
  const queryClient = useQueryClient();

  const { data: languagePacks, isLoading } = useQuery({
    queryKey: ['language-packs'],
    queryFn: async () => {
      const { data, error } = await supabase
        .from('language_packs')
        .select('*')
        .order('language_name');

      if (error) throw error;
      return data as LanguagePack[];
    },
  });

  const createLanguagePackMutation = useMutation({
    mutationFn: async (packData: CreateLanguagePackData) => {
      const { data, error } = await supabase
        .from('language_packs')
        .insert({
          ...packData,
          status: 'draft',
          completion_percentage: 0,
        })
        .select()
        .single();

      if (error) throw error;
      return data;
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['language-packs'] });
      toast.success('Language pack created successfully');
    },
    onError: (error: any) => {
      toast.error(`Failed to create language pack: ${error.message}`);
    },
  });

  const updateLanguagePackMutation = useMutation({
    mutationFn: async ({ id, updates }: { id: string; updates: Partial<LanguagePack> }) => {
      const { data, error } = await supabase
        .from('language_packs')
        .update(updates)
        .eq('id', id)
        .select()
        .single();

      if (error) throw error;
      return data;
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['language-packs'] });
      toast.success('Language pack updated successfully');
    },
    onError: (error: any) => {
      toast.error(`Failed to update language pack: ${error.message}`);
    },
  });

  const deleteLanguagePackMutation = useMutation({
    mutationFn: async (id: string) => {
      const { error } = await supabase
        .from('language_packs')
        .delete()
        .eq('id', id);

      if (error) throw error;
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['language-packs'] });
      toast.success('Language pack deleted successfully');
    },
    onError: (error: any) => {
      toast.error(`Failed to delete language pack: ${error.message}`);
    },
  });

  const exportLanguagePackMutation = useMutation({
    mutationFn: async (id: string) => {
      const pack = languagePacks?.find(p => p.id === id);
      if (!pack) throw new Error('Language pack not found');

      const exportData = {
        language_code: pack.language_code,
        language_name: pack.language_name,
        translations: pack.translations,
        exported_at: new Date().toISOString(),
      };

      const blob = new Blob([JSON.stringify(exportData, null, 2)], {
        type: 'application/json',
      });

      const url = URL.createObjectURL(blob);
      const a = document.createElement('a');
      a.href = url;
      a.download = `${pack.language_code}-language-pack.json`;
      document.body.appendChild(a);
      a.click();
      document.body.removeChild(a);
      URL.revokeObjectURL(url);

      return exportData;
    },
    onSuccess: () => {
      toast.success('Language pack exported successfully');
    },
    onError: (error: any) => {
      toast.error(`Failed to export language pack: ${error.message}`);
    },
  });

  return {
    languagePacks: languagePacks || [],
    isLoading,
    createLanguagePack: createLanguagePackMutation.mutate,
    updateLanguagePack: updateLanguagePackMutation.mutate,
    deleteLanguagePack: deleteLanguagePackMutation.mutate,
    exportLanguagePack: exportLanguagePackMutation.mutate,
    isCreating: createLanguagePackMutation.isPending,
    isUpdating: updateLanguagePackMutation.isPending,
    isDeleting: deleteLanguagePackMutation.isPending,
    isExporting: exportLanguagePackMutation.isPending,
  };
};